/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import { themed } from "@smartsheet/sds-theme-internal";
import classnames from "classnames";
import { useContext, useEffect, useMemo } from "react";
import { components } from "react-select";
import { borderRadius } from "../../../constants/borders";
import {
    danger,
    focus,
    neutral,
    neutralDark30,
    neutralDark40,
    neutralLight20,
    neutralLight30,
    neutralLight40,
} from "../../../constants/colors";
import { useTheme } from "../../../theme/utils/useTheme";
import { FOCUS_USING_BEFORE_CLASS_NAME, getFocusRingStyles } from "../../../util/focusRing";
import { IE11Flexbug3Fix } from "./IE11Flexbug3Fix";
import { useLayoutContext } from "./LayoutContext";
import { StaticPropContext } from "./StaticPropContext";
import { ValidationContext } from "./ValidationContext";

const controlTheme = themed({
    borderRadius: {
        "lodestar-light": borderRadius() + "px",
        "ursa-light": borderRadius() + "px",
    },
    border: {
        "lodestar-light": "none",
        "ursa-light": "none",
    },
    padding: {
        "lodestar-light": 4,
        "ursa-light": 4,
        // To account for the added 1px border on all sides.
    },
    background: {
        menuIsOpen: {
            "lodestar-light": neutralLight40,
            "ursa-light": neutralLight40,
        },
        menuIsClosed: {
            "lodestar-light": neutralLight30,
            "ursa-light": neutralLight30,
        },
    },
    backgroundActive: {
        "lodestar-light": neutral,
        "ursa-light": neutral,
    },
    backgroundHover: {
        menuIsOpen: {
            "lodestar-light": neutralLight40,
            "ursa-light": neutralLight40,
        },
        menuIsClosed: {
            "lodestar-light": neutralLight20,
            "ursa-light": neutralLight20,
        },
    },
    textColor: {
        menuIsOpen: neutralDark30,
        menuIsClosed: neutralDark40,
    },
});

const ringTheme = themed({
    focus: {
        outlineColor: {
            default: {
                "lodestar-light": focus,
                "ursa-light": focus,
            },
            error: {
                "lodestar-light": danger,
                "ursa-light": danger,
            },
        },
        radius: {
            "lodestar-light": 7,
            "ursa-light": 7,
        },
        offset: {
            "lodestar-light": 4,
            "ursa-light": 4,
        },
    },
    active: {
        outlineColor: {
            default: {
                "lodestar-light": focus,
                "ursa-light": focus,
            },
            error: {
                "lodestar-light": danger,
                "ursa-light": danger,
            },
        },
        radius: {
            "lodestar-light": 4,
            "ursa-light": 4,
        },
        offset: {
            "lodestar-light": 0,
            "ursa-light": 0,
        },
    },
});

/**
 * @deprecated This component is being removed in V6+, February 2025, in favor of the `SelectV2/MultiSelectV2` components.
 */
export const Control: typeof components.Control = ({ children, innerRef: providedRef, className, ...props }) => {
    const layoutContext = useLayoutContext();
    const validationState = useContext(ValidationContext);
    const { clientId } = useContext(StaticPropContext);
    const innerRef = useEnsuredForwardedRef<HTMLElement>(providedRef);

    // Using a normal effect here because though we're measuring the DOM and updating layout here,
    // the component that will be affected is the MenuPortal, not the Control.
    useEffect(() => {
        // If the control's height isn't the same as the layoutContext, notify!
        const height = innerRef.current?.offsetHeight;
        if (height && height !== layoutContext.controlHeight) {
            layoutContext.setControlHeight(height);
        }
    });

    const { key } = useTheme();
    const theme = key as ThemeKey;

    const focusRing = useMemo(
        () =>
            getFocusRingStyles({
                theme,
                offset: ringTheme.focus.offset(theme),
                radius: ringTheme.focus.radius(theme),
            }),
        [theme]
    );

    const activeRing = useMemo(
        () =>
            getFocusRingStyles({
                theme,
                colorOverride:
                    validationState === "error"
                        ? ringTheme.active.outlineColor.error(theme)
                        : ringTheme.active.outlineColor.default(theme),
                offset: ringTheme.active.offset(theme),
                radius: ringTheme.active.radius(theme),
                pseudoElement: "after",
            }),
        [theme, validationState]
    );

    const menuIsOpen = !!props.selectProps.menuIsOpen;
    return (
        <IE11Flexbug3Fix>
            <components.Control
                {...props}
                innerProps={{
                    ...props.innerProps,
                    // Yarg! React-select spreads this object onto the Control but doesn't allow an arbitrary shape.
                    // Let's just push through this since it's a safe thing to do in this case.
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    "data-client-id": clientId && `${clientId}--control`,
                }}
                innerRef={innerRef}
                className={classnames(FOCUS_USING_BEFORE_CLASS_NAME, className)}
                css={css`
                    border-radius: ${controlTheme.borderRadius(theme)};
                    border: ${controlTheme.border(theme)};
                    background: ${menuIsOpen
                        ? controlTheme.background.menuIsOpen(theme)
                        : controlTheme.background.menuIsClosed(theme)};
                    color: ${menuIsOpen
                        ? controlTheme.textColor.menuIsOpen(theme)
                        : controlTheme.textColor.menuIsClosed(theme)};
                    box-shadow: none;
                    padding: ${controlTheme.padding(theme)}px;
                    transition: background 0.2s;
                    cursor: pointer;
                    min-height: 40px;

                    &:hover {
                        background: ${menuIsOpen
                            ? controlTheme.backgroundHover.menuIsOpen(theme)
                            : controlTheme.backgroundHover.menuIsClosed(theme)};
                        border: ${controlTheme.border(theme)};
                    }
                    &:active {
                        border-color: transparent;
                        background: ${controlTheme.backgroundActive(theme)};
                    }

                    ${!menuIsOpen && props.isFocused && focusRing}

                    ${(validationState === "error" || menuIsOpen) && activeRing}
                `}
            >
                {children}
            </components.Control>
        </IE11Flexbug3Fix>
    );
};
